import { TypedData, TypedDataDomain } from "viem";
import { Address } from "viem";

export interface BungeeResponse {
  success: boolean;
  statusCode: number;
}

export interface BungeeToken {
  chainId: number;
  address: string;
  name: string;
  symbol: string;
  decimals: number;
  logoURI: string;
  balance?: string;
  balanceInUsd?: number;
}

export interface BungeeTokensListRequest {
  userAddress?: string;
  chainIds?: string;
  list?: "full" | "trending";
}
export interface BungeeTokensListResponse extends BungeeResponse {
  result: Record<string, BungeeToken[]>;
}

export interface TokenAmount {
  token: BungeeToken;
  amount: string;
  priceInUsd: number;
  valueInUsd: number;
  minAmountOut?: string;
  effectiveReceivedInUsd?: number;
}

export interface GasFee {
  gasToken: BungeeToken;
  gasLimit: string;
  gasPrice: string;
  estimatedFee: string;
  feeInUsd: number;
}

export interface RouteFee {
  token: BungeeToken;
  amount: string;
  feeInUsd: number;
  priceInUsd: number;
}

export interface DexProtocol {
  name: string;
  icon: string;
  displayName: string;
}

export interface DexDetails {
  protocol: DexProtocol;
  minAmountOut: string;
  outputTokenAddress: string;
  inputTokenAddress: string;
  amountOut: string;
  slippage: number;
}

export interface RouteDetails {
  name: string;
  logoURI: string;
  routeFee: RouteFee | null;
  dexDetails: DexDetails | null;
}

export interface ManualRoute {
  quoteId: string;
  quoteExpiry: number;
  output: TokenAmount;
  affiliateFee: unknown | null;
  approvalData: unknown | null;
  gasFee: GasFee;
  slippage: number;
  estimatedTime: number;
  routeDetails: RouteDetails;
  refuel: unknown | null;
}

export interface AutoRoute {
  userOp: string;
  requestHash: string;
  output: TokenAmount;
  requestType: RequestType;
  approvalData?: {
    spenderAddress: Address | "0";
    amount: string;
    tokenAddress: Address;
    userAddress: Address;
  };
  affiliateFee: unknown | null;
  signTypedData: unknown | null;
  gasFee: GasFee;
  slippage: number;
  suggestedClientSlippage: number;
  txData: {
    to: Address;
    data: string;
    value: string;
    chainId: number;
  };
  estimatedTime: number;
  routeDetails: RouteDetails;
  refuel: {
    output: string;
  } | null;
  quoteId: string;
  quoteExpiry: number;
}

export interface BungeeQuoteResponse extends BungeeResponse {
  result: {
    originChainId: number;
    destinationChainId: number;
    userAddress: Address;
    receiverAddress: Address;
    input: TokenAmount;
    destinationExec: unknown | null;
    autoRoute: AutoRoute | null;
    manualRoutes: ManualRoute[];
  };
}

export interface BungeeQuoteRequest {
  userAddress: string;
  originChainId: string;
  destinationChainId: string;
  inputToken: string;
  inputAmount: string;
  receiverAddress: string;
  outputToken: string;
  refuel: boolean;
}

type RequestType = "SINGLE_OUTPUT_REQUEST" | "SWAP_REQUEST";

export interface BungeeSubmitRequest {
  requestType: RequestType;
  request: unknown;
  userSignature: string;
  quoteId: string;
}

export enum RequestStatusEnum {
  PENDING = 0,
  ASSIGNED = 1,
  EXTRACTED = 2,
  FULFILLED = 3,
  SETTLED = 4,
  EXPIRED = 5,
  CANCELLED = 6,
  REFUNDED = 7,
}

export interface BungeeSubmitResponse extends BungeeResponse {
  result: {
    orderTimestamp: string;
    status: keyof typeof RequestStatusEnum;
    statusCode: RequestStatusEnum;
    requestHash: string;
    ogSender: string;
    requestSource: string;
    userSignature: string;
  };
}

export interface BungeeStatusResult {
  hash: string;
  originData: {
    input: TokenAmount[];
    originChainId: number;
    txHash: string;
    status: string;
    userAddress: string;
    timestamp: number;
  };
  destinationData: {
    output: TokenAmount[];
    destinationChainId: number;
    txHash: string;
    status: string;
    receiverAddress: string;
    timestamp: number;
  };
  routeDetails: {
    name: string;
    logoURI: string;
  };
  bungeeStatusCode: RequestStatusEnum;
  refund: {
    chainId: number;
    txHash: string;
  } | null;
}

export interface BungeeRequest {
  requestHash?: string;
  txHash?: string;
}

export interface BungeeStatusResponse extends BungeeResponse {
  result: BungeeStatusResult[];
}

export type PermitTypedData = {
  domain: TypedDataDomain;
  types: TypedData;
  values: Record<string, unknown> & {
    witness?: unknown;
  };
};
